import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle2, Loader2, Trash2, AlertTriangle, Bug, CreditCard } from "lucide-react";
import { SubscriptionStatus } from "@/components/subscription/SubscriptionStatus";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import * as Sentry from "@sentry/react";
import { api } from "@/lib/api";

// Delete Account Loading Modal Component
function DeleteAccountLoadingModal({ 
  isOpen, 
  currentStep,
  totalSteps 
}: {
  isOpen: boolean;
  currentStep: number;
  totalSteps: number;
}) {
  const steps = [
    { label: "Preparing account deletion...", icon: Loader2 },
    { label: "Removing player data...", icon: Loader2 },
    { label: "Clearing shortlists...", icon: Loader2 },
    { label: "Deleting email analytics...", icon: Loader2 },
    { label: "Finalizing deletion...", icon: Loader2 },
    { label: "Account deleted successfully!", icon: CheckCircle2 },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center space-y-6">
          {/* Header */}
          <div className="space-y-2">
            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <Trash2 className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">Deleting Account</h3>
            <p className="text-sm text-gray-600">
              Please wait while we permanently remove your account and data
            </p>
          </div>

          {/* Progress Steps */}
          <div className="space-y-4">
            {steps.map((step, index) => {
              const isCurrentStep = index === currentStep;
              const isCompleted = index < currentStep;
              const isActive = index <= currentStep;
              
              return (
                <div
                  key={index}
                  className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-300 ${
                    isActive 
                      ? isCompleted 
                        ? 'bg-green-50 border border-green-200' 
                        : 'bg-red-50 border border-red-200'
                      : 'bg-gray-50 border border-gray-200'
                  }`}
                >
                  <div className="flex-shrink-0">
                    {isCompleted ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : isCurrentStep ? (
                      <Loader2 className="w-5 h-5 text-red-600 animate-spin" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                    )}
                  </div>
                  <span className={`text-sm font-medium ${
                    isActive 
                      ? isCompleted 
                        ? 'text-green-800' 
                        : 'text-red-800'
                      : 'text-gray-500'
                  }`}>
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-600">
              <span>Progress</span>
              <span>{Math.round((currentStep / (totalSteps - 1)) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-red-600 h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${(currentStep / (totalSteps - 1)) * 100}%` }}
              />
            </div>
          </div>

          {currentStep === totalSteps - 1 && (
            <div className="text-center space-y-2">
              <p className="text-sm text-green-600 font-medium">
                Your account has been permanently deleted.
              </p>
              <p className="text-xs text-gray-500">
                Redirecting you to create a new account...
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, updateUser, logout } = useAuth();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: user?.name || "",
    school: user?.school || "",
  });
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletionStep, setDeletionStep] = useState(0);
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [showSubscriptionWarning, setShowSubscriptionWarning] = useState(false);

  // Fetch subscription status
  const { data: subscription } = useQuery({
    queryKey: ['/api/payments/subscription-status'],
    queryFn: async () => {
      const response = await api.get('/api/payments/subscription-status');
      return response.data;
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: { name?: string; school?: string }) => {
      const response = await apiRequest("PUT", "/api/auth/update-profile", data);
      return response.json();
    },
    onSuccess: (data) => {
      updateUser(data.user);
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update failed",
        description: error.message || "Failed to update profile",
        variant: "destructive",
      });
    },
  });

  // Delete account mutation with enhanced UX
  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      // Show deletion modal and start progress
      setShowDeletionModal(true);
      setDeletionStep(0);
      
      // Simulate step progression for better UX
      const steps = [
        () => new Promise(resolve => setTimeout(() => { setDeletionStep(1); resolve(undefined); }, 300)),
        () => new Promise(resolve => setTimeout(() => { setDeletionStep(2); resolve(undefined); }, 500)),
        () => new Promise(resolve => setTimeout(() => { setDeletionStep(3); resolve(undefined); }, 400)),
        () => new Promise(resolve => setTimeout(() => { setDeletionStep(4); resolve(undefined); }, 600)),
      ];
      
      // Execute deletion steps
      for (const step of steps) {
        await step();
      }
      
      // Make the actual API call
      const response = await apiRequest("DELETE", "/api/auth/delete-account");
      return response.json();
    },
    onSuccess: () => {
      // Show final success step
      setDeletionStep(5);
      
      // Show success toast
      toast({
        title: "Account deleted",
        description: "Your account has been permanently deleted.",
      });
      
      // Wait a moment for the success animation, then logout and redirect
      setTimeout(() => {
        setShowDeletionModal(false);
        logout();
      }, 2000);
    },
    onError: (error: any) => {
      setShowDeletionModal(false);
      setDeletionStep(0);
      toast({
        title: "Deletion failed",
        description: error.message || "Failed to delete account",
        variant: "destructive",
      });
    },
  });

  const handleProfileUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate(formData);
  };

  const handleDeleteAccount = () => {
    if (deleteConfirmText !== "DELETE") {
      toast({
        title: "Invalid confirmation",
        description: "Please type 'DELETE' to confirm account deletion.",
        variant: "destructive",
      });
      return;
    }

    // Check if user has active subscription
    if (subscription?.has_active_subscription && subscription?.status === 'active') {
      setShowSubscriptionWarning(true);
      return;
    }

    deleteAccountMutation.mutate();
  };

  const isDeleteConfirmValid = deleteConfirmText === "DELETE";

  return (
    <>
      {/* Delete Account Loading Modal */}
      <DeleteAccountLoadingModal 
        isOpen={showDeletionModal}
        currentStep={deletionStep}
        totalSteps={6}
      />

      {/* Subscription Warning Dialog */}
      <AlertDialog open={showSubscriptionWarning} onOpenChange={setShowSubscriptionWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-orange-600" />
              Active Subscription Detected
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You cannot delete your account while you have an active subscription.
              </p>
              <p>
                Please cancel your subscription first and wait until the billing period ends before deleting your account.
              </p>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium text-orange-800">
                  Current subscription details:
                </p>
                <ul className="text-sm text-orange-700 space-y-1">
                  <li>• Plan: {subscription?.plan === 'annual' ? 'Annual ($72/month)' : 'Monthly ($90/month)'}</li>
                  <li>• Status: {subscription?.status}</li>
                  {subscription?.ends_at && (
                    <li>• Renews on: {new Date(subscription.ends_at).toLocaleDateString()}</li>
                  )}
                </ul>
              </div>
              <p className="text-sm text-gray-600">
                After canceling, you'll retain access until the end of your billing period, then you can delete your account.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmText("")}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowSubscriptionWarning(false);
                setDeleteConfirmText("");
                // Navigate to subscription tab
                const subscriptionTab = document.querySelector('[value="subscription"]');
                if (subscriptionTab) {
                  (subscriptionTab as HTMLButtonElement).click();
                }
              }}
              className="bg-orange-600 hover:bg-orange-700"
            >
              Go to Subscription Settings
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="px-4 py-6 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-neutral-800">Profile Settings</h1>
          <p className="text-sm text-neutral-500">Manage your account settings and preferences</p>
        </div>

        <Tabs defaultValue="account" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="account" className="text-xs sm:text-sm">
              <span className="sm:hidden">Account</span>
              <span className="hidden sm:inline">Account</span>
            </TabsTrigger>
            <TabsTrigger value="subscription" className="text-xs sm:text-sm">
              <span className="sm:hidden">Plan</span>
              <span className="hidden sm:inline">Subscription</span>
            </TabsTrigger>
            <TabsTrigger value="danger" className="text-xs sm:text-sm">
              <span className="sm:hidden">Manage</span>
              <span className="hidden sm:inline">Account Management</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="account" className="space-y-6">
            {/* Account Information */}
            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
                <CardDescription>
                  Update your basic account information and school affiliation.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={user?.email || ""}
                      disabled
                      className="bg-neutral-50"
                    />
                    <p className="text-xs text-neutral-500">Email cannot be changed</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      value={user?.username || ""}
                      disabled
                      className="bg-neutral-50"
                    />
                    <p className="text-xs text-neutral-500">Username cannot be changed</p>
                  </div>
                </div>

                <form onSubmit={handleProfileUpdate} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your full name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="school">School/Organization</Label>
                    <Input
                      id="school"
                      value={formData.school}
                      onChange={(e) => setFormData({ ...formData, school: e.target.value })}
                      placeholder="Your school or organization"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={updateProfileMutation.isPending}
                    className="w-full md:w-auto"
                  >
                    {updateProfileMutation.isPending ? "Updating..." : "Update Profile"}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Account Status */}
            <Card>
              <CardHeader>
                <CardTitle>Account Status</CardTitle>
                <CardDescription>
                  Your current account information and authentication method.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Authentication Method</span>
                  <Badge variant={user?.oauth_provider ? "secondary" : "default"}>
                    {user?.oauth_provider ? `OAuth (${user.oauth_provider})` : "Email/Password"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Account Type</span>
                  <Badge variant="outline">Standard User</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Member Since</span>
                  <span className="text-sm text-neutral-600">
                    {user?.subscription_created_at ? new Date(user.subscription_created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : "Unknown"}
                  </span>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscription" className="space-y-6">
            <SubscriptionStatus />
          </TabsContent>

          <TabsContent value="danger" className="space-y-6">
            {/* Danger Zone */}
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-600">Danger Zone</CardTitle>
                <CardDescription>
                  Irreversible and destructive actions for your account.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Delete Account */}
                <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                  <h3 className="text-lg font-semibold text-red-800 mb-2">Delete Account</h3>
                  <p className="text-sm text-red-700 mb-4">
                    Once you delete your account, there is no going back. This will permanently delete your account, 
                    all player data, shortlists, email analytics, and any other associated data.
                  </p>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm">
                        Delete Account
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3">
                          <p>
                            This action cannot be undone. This will permanently delete your account and 
                            remove all of your data from our servers.
                          </p>
                          <p>
                            <strong>This includes:</strong>
                          </p>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            <li>All player profiles and data</li>
                            <li>All shortlists and saved players</li>
                            <li>All email templates and content</li>
                            <li>All file attachments</li>
                            <li>Email analytics and history</li>
                            <li>OAuth email data</li>
                            <li>Account preferences and settings</li>
                            <li>Payment and subscription history</li>
                            <li>All feedback and ratings</li>
                          </ul>
                          <div className="mt-4">
                            <Label htmlFor="deleteConfirm" className="text-sm font-medium">
                              Type <strong>DELETE</strong> to confirm:
                            </Label>
                            <Input
                              id="deleteConfirm"
                              value={deleteConfirmText}
                              onChange={(e) => setDeleteConfirmText(e.target.value)}
                              placeholder="Type DELETE here"
                              className="mt-1"
                            />
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setDeleteConfirmText("")}>
                          Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDeleteAccount}
                          disabled={!isDeleteConfirmValid || deleteAccountMutation.isPending}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          {deleteAccountMutation.isPending ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            "Delete Account"
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
} 